export interface GeocodeSuggestion {
    label: string;
    lat: number;
    lon: number;
}

const PHOTON_BASE = 'https://photon.komoot.io';

// Only show pickable places — drop states, counties, countries, regions, etc.
// Cities, towns, villages, hamlets, suburbs/neighbourhoods, and ZIP codes pass.
const ALLOWED_PLACE_TYPES = new Set([
    'city',
    'town',
    'village',
    'hamlet',
    'suburb',
    'neighbourhood',
    'postcode',
]);

// Photon returns full state names ("New York"); convert to USPS 2-letter codes
// for the Vivid-style "New York, NY, USA" label.
const US_STATE_CODES: Record<string, string> = {
    'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR',
    'California': 'CA', 'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE',
    'Florida': 'FL', 'Georgia': 'GA', 'Hawaii': 'HI', 'Idaho': 'ID',
    'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA', 'Kansas': 'KS',
    'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
    'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS',
    'Missouri': 'MO', 'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV',
    'New Hampshire': 'NH', 'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY',
    'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH', 'Oklahoma': 'OK',
    'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
    'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT',
    'Vermont': 'VT', 'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV',
    'Wisconsin': 'WI', 'Wyoming': 'WY',
    'District of Columbia': 'DC',
    'Puerto Rico': 'PR', 'Guam': 'GU', 'U.S. Virgin Islands': 'VI',
};

const isPickablePlace = (props: any): boolean => {
    return props?.osm_key === 'place' && ALLOWED_PLACE_TYPES.has(props?.osm_value);
};

const formatLabel = (props: any, fallback: string): string => {
    if (!props) return fallback;
    const city = props.city || props.name;
    const isUS = props.countrycode === 'US';
    const stateName = props.state;
    const stateCode = isUS && stateName ? (US_STATE_CODES[stateName] ?? stateName) : stateName;

    const parts: string[] = [];
    if (city) parts.push(city);
    if (stateCode && stateCode !== city) parts.push(stateCode);
    if (isUS) {
        parts.push('USA');
    } else if (props.country) {
        parts.push(props.country);
    }
    return parts.join(', ') || fallback;
};

export const geocodeQuery = async (q: string): Promise<GeocodeSuggestion[]> => {
    const url = `${PHOTON_BASE}/api/?q=${encodeURIComponent(q)}&limit=15&lang=en`;
    const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
    if (!res.ok) return [];
    const data = await res.json();
    const features = data?.features ?? [];
    return features
        .filter((f: any) => isPickablePlace(f.properties))
        .slice(0, 6)
        .map((f: any) => {
            const [lon, lat] = f.geometry?.coordinates ?? [];
            const props = f.properties ?? {};
            return {
                label: formatLabel(props, props.name ?? q),
                lat: typeof lat === 'number' ? lat : parseFloat(lat),
                lon: typeof lon === 'number' ? lon : parseFloat(lon),
            };
        })
        .filter((s: GeocodeSuggestion) => !Number.isNaN(s.lat) && !Number.isNaN(s.lon));
};

export const reverseGeocode = async (lat: number, lon: number): Promise<string> => {
    try {
        const url = `${PHOTON_BASE}/reverse?lat=${lat}&lon=${lon}&lang=en`;
        const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
        if (!res.ok) return 'Current Location';
        const data = await res.json();
        const props = data?.features?.[0]?.properties;
        return formatLabel(props, 'Current Location');
    } catch {
        return 'Current Location';
    }
};
